/**
 * Risk Scorecard Card — 8 risk dimensions in a grid
 *
 * Each dimension shows: name, R/Y/G dot, score bar (1-5), trend arrow.
 * Collapsible to show evidence for each dimension.
 */

import { useState } from 'react';
import type { RiskDimension, RiskScorecard, RiskStatus, TrendDirection } from '../../../types';

interface RiskScorecardCardProps {
  scorecard: RiskScorecard;
}

function StatusDot({ status }: { status: RiskStatus }) {
  const colors: Record<RiskStatus, string> = {
    green: 'bg-green-500',
    yellow: 'bg-yellow-400',
    red: 'bg-red-500',
  };

  return (
    <span
      className={`inline-block h-2.5 w-2.5 rounded-full ${colors[status]}`}
      aria-label={`Status: ${status}`}
    />
  );
}

function ScoreBar({ score }: { score: number }) {
  return (
    <div className="flex gap-0.5" aria-label={`Risk score: ${score} out of 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          className={`h-1.5 w-4 rounded-sm ${
            i <= score
              ? score <= 2
                ? 'bg-green-400'
                : score <= 3
                  ? 'bg-yellow-400'
                  : 'bg-red-400'
              : 'bg-gray-200'
          }`}
        />
      ))}
    </div>
  );
}

function TrendIndicator({ trend }: { trend: TrendDirection }) {
  const config: Record<TrendDirection, { arrow: string; color: string; label: string }> = {
    up: { arrow: '\u2191', color: 'text-red-500', label: 'Risk increasing' },
    down: { arrow: '\u2193', color: 'text-green-500', label: 'Risk decreasing' },
    stable: { arrow: '\u2192', color: 'text-gray-400', label: 'Stable' },
  };

  const { arrow, color, label } = config[trend] || config.stable;

  return (
    <span className={`text-sm ${color}`} title={label} aria-label={label}>
      {arrow}
    </span>
  );
}

function DimensionCard({ dimension }: { dimension: RiskDimension }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className="rounded-md border border-gray-100 bg-white p-3 hover:shadow-sm transition-shadow cursor-pointer"
      onClick={() => setExpanded(!expanded)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && setExpanded(!expanded)}
      data-testid="risk-dimension"
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <StatusDot status={dimension.status} />
          <span className="text-sm font-medium text-gray-900">{dimension.name}</span>
        </div>
        <TrendIndicator trend={dimension.trend} />
      </div>
      <ScoreBar score={dimension.score} />

      {/* Expandable evidence */}
      {expanded && dimension.evidence.length > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-100">
          <ul className="space-y-1">
            {dimension.evidence.map((ev, i) => (
              <li key={i} className="text-xs text-gray-500 flex items-start gap-1.5">
                <span className="text-gray-300 mt-0.5">-</span>
                <span>{ev}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function OverallRiskBadge({ level }: { level: RiskStatus }) {
  const config: Record<RiskStatus, { label: string; bg: string; text: string }> = {
    green: { label: 'Low Risk', bg: 'bg-green-100', text: 'text-green-800' },
    yellow: { label: 'Moderate Risk', bg: 'bg-yellow-100', text: 'text-yellow-800' },
    red: { label: 'High Risk', bg: 'bg-red-100', text: 'text-red-800' },
  };

  const { label, bg, text } = config[level] || config.yellow;

  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${bg} ${text}`}
      data-testid="overall-risk-badge"
    >
      {label}
    </span>
  );
}

export function RiskScorecardCard({ scorecard }: RiskScorecardCardProps) {
  if (scorecard.dimensions.length === 0) {
    return null;
  }

  return (
    <div
      className="rounded-lg border border-gray-200 bg-white p-5"
      data-testid="risk-scorecard-card"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900">Risk Scorecard</h3>
        <OverallRiskBadge level={scorecard.overall_risk_level} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {scorecard.dimensions.map((dim) => (
          <DimensionCard key={dim.name} dimension={dim} />
        ))}
      </div>
    </div>
  );
}
